import React, {useCallback, useEffect} from 'react';
import EmptyList from "../../../../../components/EmptyList/EmptyList";
import s from "../../flows.module.scss";
import CustomSkeleton from "../../../../../components/UiKit/CustomSkeleton/CustomSkeleton";
import {useDispatch, useSelector} from "react-redux";
import {
    assignFlowToTeamMember,
    deleteFlow,
    fetchFlowsList,
    flowSelector,
    setNote
} from "../../../../../redux/slices/flowSlice";
import {globalDataSelector} from "../../../../../redux/slices/globalDataSlice";
import {useSearchParams} from "react-router-dom";
import {
    INVALID_PAGE_ERROR,
    snackbar_error,
    snackbar_info,
    snackbar_success
} from "../../../../../utils/vars/staticVariables";
import useCustomSnackbar from "../../../../../hooks/useCustomSnackbar";
import {useSnackbar} from "notistack";
import {useTranslation} from "react-i18next";
import {flowsActiveTabColumnsList} from "../../../../../utils/vars/params";
import {page} from "../../../Statistics/IOS/IOSClicksStats/IOSClicksStats";
import useSetSearchParams from "../../../../../hooks/useSetSearchParams";
import ActiveTd from "./ActiveTd";
import {getTableDefaultParams} from "../../../../../api/helpers/scripts";
import {
    no_permission_to_replace_owner,
    not_member_of_your_team,
    redirectors_in_flow_do_not_belong_to_user
} from "../../../../../utils/vars/errorMessages";

const ActiveTab = ({onDuplicateFlowHandler}) => {
    const {flowsListPending, flowsList, totalCount} = useSelector(flowSelector);
    const {geoListPending} = useSelector(globalDataSelector);

    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const updateSearchParams = useSetSearchParams(setSearchParams);
    const showSnackbar = useCustomSnackbar();
    const {closeSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const fetchActiveFlows = useCallback(() => {
        const promise = dispatch(
            fetchFlowsList(
                getTableDefaultParams({
                    searchParams,
                    defaultPageSize: 25,
                }),
            ),
        );

        promise.unwrap().catch((err) => {
            if (err?.message === INVALID_PAGE_ERROR) {
                updateSearchParams(page, 1);
            }
        });

        return promise;
    }, [dispatch, searchParams, updateSearchParams]);

    const onDeleteHandler = useCallback(
        async (id, domain) => {
            const loadingSnackbarKey = showSnackbar(
                t("notif_delete_flow"),
                snackbar_info,
                true,
            );

            const flowDeleteResult = await dispatch(deleteFlow({id}));

            if (flowDeleteResult?.payload?.status === 200) {
                fetchActiveFlows();
                closeSnackbar(loadingSnackbarKey);
                showSnackbar(
                    `${domain} ${t("notif_was_deleted")}`,
                    snackbar_success,
                );
            } else {
                closeSnackbar(loadingSnackbarKey);
                showSnackbar(
                    `${t("notif_failed_to_delete")} ${domain} ${t("notif_lowercase_flow")}`,
                    snackbar_error,
                );
            }
        },
        [closeSnackbar, dispatch, fetchActiveFlows, showSnackbar],
    );

    const assignFlowToTeamMemberHandler = useCallback(async (form) => {
        const assignFlowSnackbarPending = showSnackbar(
            t("assigning_flow_to_user"),
            snackbar_info,
            true,
        );

        const {data, status} = await dispatch(assignFlowToTeamMember(form)).unwrap();

        if (status === 200) {
            closeSnackbar(assignFlowSnackbarPending)
            fetchActiveFlows();
            showSnackbar(t("flow_assigned"), snackbar_success);
        } else if (data?.detail === not_member_of_your_team) {
            closeSnackbar(assignFlowSnackbarPending)
            showSnackbar(t("not_member_of_your_team"), snackbar_error);
        } else if (data?.detail === no_permission_to_replace_owner) {
            closeSnackbar(assignFlowSnackbarPending)
            showSnackbar(t("no_permission_to_replace_owner"), snackbar_error);
        } else if (data?.detail === redirectors_in_flow_do_not_belong_to_user) {
            closeSnackbar(assignFlowSnackbarPending)
            showSnackbar(t("redirectors_in_flow_do_not_belong_to_user"), snackbar_error);
        } else {
            showSnackbar(t("failed_assign_flow"), snackbar_error);
        }
    }, [dispatch, fetchActiveFlows, showSnackbar, t]);

    const editFlowName = useCallback(async (form, setActiveSetNotePopup) => {
        const setNoteResult = await dispatch(setNote(form));

        if (setNoteResult?.payload?.status === 200) {
            setActiveSetNotePopup(false);
            fetchActiveFlows();
            showSnackbar(t("notif_renamed_flow"), snackbar_success);
        } else {
            showSnackbar(t("notif_failed_flow_renaming"), snackbar_error);
        }
    }, [dispatch, fetchActiveFlows, showSnackbar, t]);

    useEffect(() => {
        const promise = fetchActiveFlows();

        return () => {
            promise.abort();
        };
    }, [dispatch, searchParams]);

    const getTooltip = useCallback((ref, index, daysToExpiry) => (
        <div
            ref={ref}
            style={
                index === 0
                    ? {transform: 'translateY(-30%)'}
                    : index >= flowsList.length - 4
                        ? {transform: 'translateY(-90%)'}
                        : null
            }
            className={`${s.domainTooltip} ${daysToExpiry > 1 ? s.orange : s.red}`}
        >
            {
                daysToExpiry < 2 && <span>{t('one_day_expiring_domain_text')}</span>
            }

            {
                daysToExpiry > 1 && <>
                    <span className={s.orangeUpperText}>
                        <span className={s.bold}>{t('attention')}</span>
                        {" "}
                        {t('domain_expires_in')}
                        {" "}
                        {daysToExpiry}
                        {" "}
                        {daysToExpiry > 4 && t('days_word_case_iv')}
                        {daysToExpiry > 1 && daysToExpiry < 5 && t('days_word_case_i')}
                        .
                        {" "}
                        {t('to_extend_domain')}
                    </span>
                    <span className={s.lowerOrangeText}>
                        {t('not_planing_to_renew')}
                    </span>
                </>
            }
        </div>
    ), [flowsList.length, t])

    const activeTabTableThead =
        <thead>
        <tr>
            {flowsActiveTabColumnsList.map(({name}) => (
                <th key={name}>
                    <div className={s.headerContent}>
                        {t(name)}
                    </div>
                </th>
            ))}
        </tr>
        </thead>

    return (
        <>
            {totalCount === 0 && !flowsListPending ? (
                <EmptyList/>
            ) : flowsListPending || geoListPending ? (
                <section className={s.body}>
                    <table>
                        {activeTabTableThead}
                    </table>
                    <div className={s.skeletonWrapper}>
                        <CustomSkeleton styles={s.skeletonTable}/>
                    </div>
                </section>
            ) : (
                <section className={s.body}>
                    <table>
                        {activeTabTableThead}
                        <tbody>
                        {flowsList.map((item, index) => (
                            <ActiveTd
                                {...item}
                                key={item.id}
                                index={index}
                                onDeleteHandler={onDeleteHandler}
                                onDuplicateFlowHandler={onDuplicateFlowHandler}
                                getTooltip={getTooltip}
                                editFlowName={editFlowName}
                                assignFlowToTeamMemberHandler={assignFlowToTeamMemberHandler}
                            />
                        ))}
                        </tbody>
                    </table>
                </section>
            )}
        </>
    )
}

export default ActiveTab;